import { BuildingOffice2Icon } from '@heroiconsv2/react/24/outline'
import { formatPrice } from 'lib/utils'
import { calculateBookingAjarleeAmount, getBookingDiscountBreakdown, getInsuranceDisplay, getInsuranceTypeLabel } from '../bookingDetailHelpers'
import AjarleePercentageBaseTag from './AjarleePercentageBaseTag'
import BookingDiscountedAmount from './BookingDiscountedAmount'
import InsuranceOfficeNote from './InsuranceOfficeNote'
import { Band, MoneyRow, Panel, SectionCard } from './BookingSection'

const BookingCompanyPaymentCard = ({ bookingData, t, isCompanyRole, hasAdminAccess }) => {
  if (isCompanyRole && !hasAdminAccess) return null

  const discounts = getBookingDiscountBreakdown(bookingData)
  const insurance = getInsuranceDisplay(bookingData)
  const insuranceTypeLabel = getInsuranceTypeLabel(bookingData, t)

  const omr = value => `${formatPrice(value || 0, true)} ${t('common.omr')}`
  const companyTotal = Number(bookingData?.companyTotal) || 0
  const ajarleeAmount = Number(calculateBookingAjarleeAmount(bookingData)) || 0
  const showPreDiscount = bookingData?.extendBookingPreDiscountTotalPrice != null && Number(bookingData.extendBookingPreDiscountTotalPrice) > companyTotal

  return (
    <SectionCard icon={BuildingOffice2Icon} tone="sky" title={t('booking.sections.companyPaymentSummary')}>
      <Panel className="py-3">
        <MoneyRow label={t('booking.orderSummary.priceWithoutTax')} sign="">
          <BookingDiscountedAmount discount={discounts.daily} t={t} />
        </MoneyRow>
        <MoneyRow label={t('booking.orderSummary.deliveryFee')} sign="">
          <BookingDiscountedAmount discount={discounts.delivery} t={t} />
        </MoneyRow>
        {insurance.show && !insurance.collectAtOffice && (
          <MoneyRow
            label={
              <>
                {t('booking.orderSummary.insuranceFee')}
                {insurance.hasSplitType && insuranceTypeLabel && <span className="ml-1 rtl:ml-0 rtl:mr-1 text-slate-500">({insuranceTypeLabel})</span>}
              </>
            }
            sign=""
          >
            {omr(insurance.displayAmount)}
          </MoneyRow>
        )}
        {(bookingData?.extras || []).map(extra => (
          <MoneyRow key={extra.key} label={t(`booking.orderSummary.extra_${extra.key}`)} sign="">
            {omr(extra.amount)}
          </MoneyRow>
        ))}
        <MoneyRow label={t('booking.orderSummary.vat')} sign="">
          {omr(bookingData?.companyVat)}
        </MoneyRow>

        <div className="my-2 border-t border-slate-200" />

        <MoneyRow label={t('booking.orderSummary.total')} bold sign="">
          {showPreDiscount ? (
            <>
              <span className="mr-2 rtl:mr-0 rtl:ml-2 font-normal text-slate-400 line-through">{omr(bookingData.extendBookingPreDiscountTotalPrice)}</span>
              <span className="text-emerald-600">{omr(companyTotal)}</span>
            </>
          ) : (
            omr(companyTotal)
          )}
        </MoneyRow>
        <MoneyRow
          label={`${t('common.ajarleeName')} ${bookingData?.ajarleePercentage != null ? formatPrice(bookingData.ajarleePercentage, true) : '0.000'}%:`}
          sign="-"
          signClassName="text-rose-600"
        >
          <span className="text-rose-600">{omr(ajarleeAmount)}</span>
        </MoneyRow>
        <AjarleePercentageBaseTag bookingData={bookingData} t={t} variant="sky" />

        <Band label={`${t('booking.orderSummary.companyAmount')}:`} tone="sky">
          {omr(Math.max(0, companyTotal - ajarleeAmount))}
        </Band>

        <InsuranceOfficeNote bookingData={bookingData} t={t} />
      </Panel>
    </SectionCard>
  )
}

export default BookingCompanyPaymentCard
